import { getFieldErrors } from '@utils/graphqlError';

import type { ApolloError } from '@apollo/client';
import type { RegisterFailure } from '@utils/parseRegisterResponse';

/**
 * TA-10140 (QA round 2): registration was the one priority form left on the
 * old error path — it picked `Object.keys(result.fieldErrors)[0]` and then
 * compared `result.message` against whole English sentences ("Email has
 * already been taken") to decide which inline error to show. Two problems
 * QA called out:
 *  - only the FIRST backend field ever reached the form, so a response
 *    listing e.g. both password and phone showed one of them and silently
 *    dropped the other (the email+phone combination needed its own hardcoded
 *    branch for exactly this reason);
 *  - the branch keyed on backend copy, which is the fragility TA-10138 exists
 *    to remove.
 *
 * This module maps every backend field key onto the form's logical fields, in
 * the same shape as `projectFormFieldErrors.ts` / `taskFormFieldErrors.ts`.
 * It is a plain function in its own module so it can be unit-tested without
 * mounting RegisterForm's navigation stack.
 */
export type RegisterErrorField =
  | 'email'
  | 'password'
  | 'phone'
  | 'verificationCode'
  | 'firstName';

/** Backend field name (snake_case) → this form's logical field. */
const REGISTER_FIELD_NAME_MAP: Record<string, RegisterErrorField> = {
  email: 'email',
  password: 'password',
  phone_number: 'phone',
  phone: 'phone',
  email_verification_code: 'verificationCode',
};

/**
 * `updateMe` (the Apple signup path) validates profile attributes, so it can
 * report a `name` error the REST registration endpoint never returns. Kept
 * separate so the REST mapping above stays byte-for-byte what it was.
 */
const MUTATION_FIELD_NAME_MAP: Record<string, RegisterErrorField> = {
  ...REGISTER_FIELD_NAME_MAP,
  name: 'firstName',
};

/**
 * Fields whose "already taken" case gets dedicated copy pointing the user at
 * login, rather than echoing the backend's bare "has already been taken".
 */
const ALREADY_TAKEN = 'has already been taken';
const ALREADY_TAKEN_COPY: Partial<Record<RegisterErrorField, string>> = {
  email: 'This email already exists. Please use other email or login.',
  phone:
    'This phone number already exists. Please use other phone number or login.',
};

/** Web's long-standing fallback copy; native passes its own (see below). */
const DEFAULT_MESSAGE = 'error creating account.';

/**
 * Legacy fallback: older backends return only a top-level `error` sentence
 * with no per-field body. Per TA-10138, string matching stays until the P01
 * rollout is confirmed on every environment — but it now lives here, in one
 * tested place, instead of being spread across two RegisterForm variants.
 */
const fieldsFromLegacyMessage = (
  message: string
): Partial<Record<RegisterErrorField, string>> => {
  const lower = message.toLowerCase();
  const result: Partial<Record<RegisterErrorField, string>> = {};
  if (lower.includes('email has already been taken')) {
    result.email = ALREADY_TAKEN_COPY.email;
  }
  if (lower.includes('phone number has already been taken')) {
    result.phone = ALREADY_TAKEN_COPY.phone;
  }
  return result;
};

/**
 * Maps a failed registration response onto per-field inline error messages.
 * Always returns at least one entry so a validation failure can never fall
 * through to a silent form (AC: no VALIDATION_FAILED reaches a generic toast).
 */
export const getRegisterFieldErrors = (
  result: RegisterFailure,
  defaultMessage: string = DEFAULT_MESSAGE
): Partial<Record<RegisterErrorField, string>> => {
  const mapped: Partial<Record<RegisterErrorField, string>> = {};

  Object.entries(result.fieldErrorsRaw ?? {}).forEach(
    ([backendField, messages]) => {
      const field = REGISTER_FIELD_NAME_MAP[backendField];
      const rawMessage = messages?.[0];
      if (!field || !rawMessage) return;
      const takenCopy = ALREADY_TAKEN_COPY[field];
      mapped[field] =
        takenCopy && rawMessage.toLowerCase().includes(ALREADY_TAKEN)
          ? takenCopy
          : (result.fieldErrors[backendField] ?? rawMessage);
    }
  );

  if (Object.keys(mapped).length > 0) return mapped;

  const legacy = result.message ? fieldsFromLegacyMessage(result.message) : {};
  if (Object.keys(legacy).length > 0) return legacy;

  // Nothing field-scoped came back: surface the server message on the email
  // field, which is the first step of the flow the user is sent back to.
  return { email: result.message ?? defaultMessage };
};

/**
 * How far back the web signup wizard must navigate to put the errored field
 * on screen. Steps: email (0) → verification code (1) → phone (2), so an
 * error on a later step needs a shorter rewind. Earliest errored field wins,
 * matching the previous switch's ordering.
 */
export const getRegisterHistoryOffset = (
  fields: Partial<Record<RegisterErrorField, string>>,
  xstep: number
): number | null => {
  if (fields.email || fields.password) return -xstep;
  // first_name lives on the same profile step as phone (CreateProfile.web).
  if (fields.phone || fields.firstName)
    return -xstep + 2 < 0 ? -xstep + 2 : null;
  if (fields.verificationCode) return -xstep + 1 < 0 ? -xstep + 1 : null;
  return -xstep;
};

/**
 * TA-10140 (QA round 3): the Apple signup path does not hit the REST endpoint
 * at all — it calls the `updateMe` GraphQL mutation, whose `onError` was still
 * `console.log(e)` + `alert(e)`, i.e. a raw ApolloError object shown to the
 * user and no field mapping whatsoever. That is the one genuine GraphQL
 * mutation inside registration, so it is exactly what the ticket's
 * `getFieldErrors(error)` requirement is about.
 *
 * Returns `{}` when the error carries no `field_errors`, so the caller can
 * fall back to a readable message instead of inventing a field.
 */
export const getRegisterMutationFieldErrors = (
  error: ApolloError
): Partial<Record<RegisterErrorField, string>> => {
  const mapped: Partial<Record<RegisterErrorField, string>> = {};
  Object.entries(getFieldErrors(error)).forEach(([backendField, messages]) => {
    const field = MUTATION_FIELD_NAME_MAP[backendField];
    const rawMessage = messages?.[0];
    if (!field || !rawMessage) return;
    const takenCopy = ALREADY_TAKEN_COPY[field];
    mapped[field] =
      takenCopy && rawMessage.toLowerCase().includes(ALREADY_TAKEN)
        ? takenCopy
        : rawMessage;
  });
  return mapped;
};
